import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageCarousel from '../components/ImageCarousel';
import ImageLightbox from '../components/ImageLightbox';
import PriceDisplay from '../components/PriceDisplay';
import PincodeCheck from '../components/PincodeCheck';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Star, ShoppingCart, Heart, Zap, ZoomIn, Truck, Plus, Minus, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const variantLabel = (v) => Object.entries(v || {})
  .filter(([, val]) => val !== null && val !== undefined && val !== '')
  .map(([k, val]) => `${k}: ${val}`).join(', ');

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  // Map: variant index → quantity (0 means "not selected"). When no variants on the
  // product, we use index `-1` (the "no variant" line).
  const [variantQuantities, setVariantQuantities] = useState({ '-1': 1 });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [subRatings, setSubRatings] = useState({ quality: 5, packaging: 5, value_for_money: 5, taste_aroma: 5 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [selectedThumb, setSelectedThumb] = useState(0);
  const [deliveryConfig, setDeliveryConfig] = useState({ free_threshold: 500, charge: 60 });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/delivery/config`).then(r => setDeliveryConfig(r.data)).catch(() => {});
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`
      );
      setProduct(data);
      // Initialise the qty map: pre-select the first variant with qty=1, the rest 0.
      if (data.variants && data.variants.length > 0) {
        const init = {};
        data.variants.forEach((_, i) => { init[i] = i === 0 ? 1 : 0; });
        setVariantQuantities(init);
      } else {
        setVariantQuantities({ '-1': 1 });
      }
    } catch (error) {
      toast.error('Failed to load product');
    }
  };

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/reviews/${productId}`
      );
      setReviews(data);
    } catch (error) {
      console.error('Failed to load reviews');
    }
  };

  const totalSelectedQty = Object.values(variantQuantities).reduce((s, q) => s + (Number(q) || 0), 0);

  const setVariantQty = (key, qty) => {
    const stock = Math.max(0, Number(product?.stock || 0));
    const others = Object.entries(variantQuantities).reduce((s, [k, q]) => s + (k === String(key) ? 0 : Number(q || 0)), 0);
    const capped = Math.max(0, Math.min(Number(qty || 0), Math.max(0, stock - others)));
    setVariantQuantities((prev) => ({ ...prev, [key]: capped }));
  };

  const addToCart = async () => {
    if (!user) { navigate('/login'); return false; }
    const hasVariants = !!(product?.variants && product.variants.length > 0);
    const lines = [];
    if (hasVariants) {
      product.variants.forEach((v, i) => {
        const q = Number(variantQuantities[i] || 0);
        if (q > 0) lines.push({ variant: v, quantity: q });
      });
    } else {
      const q = Number(variantQuantities['-1'] || 0);
      if (q > 0) lines.push({ variant: null, quantity: q });
    }
    if (lines.length === 0) {
      toast.error('Select at least one variant and set its quantity');
      return false;
    }
    let added = 0;
    let failed = false;
    // Sequential awaits guarantee every line is in the cart before the caller proceeds
    // (e.g. before "Buy Now" navigates to /cart and reads the basket totals).
    for (const ln of lines) {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/cart`,
          { product_id: productId, quantity: ln.quantity, variant: ln.variant },
          { withCredentials: true }
        );
        added += ln.quantity;
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to add ${variantLabel(ln.variant) || 'item'}`);
        failed = true;
        break;
      }
    }
    if (added > 0) {
      toast.success(`Added ${added} item${added === 1 ? '' : 's'} to cart (${lines.length} variant${lines.length === 1 ? '' : 's'})`);
    }
    return !failed && added > 0;
  };

  const addToWishlist = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/wishlist/${productId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Added to wishlist');
    } catch (error) {
      toast.error('Failed to add to wishlist');
    }
  };

  const submitReview = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/reviews`,
        { product_id: productId, rating, comment, ...subRatings },
        { withCredentials: true }
      );
      toast.success('Thanks for your review — it\'s live now.');
      setComment('');
      setSubRatings({ quality: 5, packaging: 5, value_for_money: 5, taste_aroma: 5 });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  if (!product) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen">
      <Header />

      <div className="container mx-auto px-4 py-12 pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Image Gallery */}
          <div data-testid="product-image-gallery">
            {(() => {
              const allImages = (product.images && product.images.length > 0) ? product.images : [product.image_url];
              return (
                <>
                  <div
                    className="rounded-3xl overflow-hidden bg-white border border-[#E0E0E0] cursor-zoom-in relative group mb-3"
                    onClick={() => { setLightboxIndex(selectedThumb); setLightboxOpen(true); }}
                    data-testid="product-main-image"
                  >
                    <img src={allImages[selectedThumb]} alt={product.name} className="w-full aspect-square object-cover" data-testid="product-detail-image" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
                      <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                    </div>
                  </div>
                  {allImages.length > 1 && (
                    <div className="flex gap-2" data-testid="product-thumbnails">
                      {allImages.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedThumb(idx)}
                          className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === selectedThumb ? 'border-primary' : 'border-[#E0E0E0] opacity-60 hover:opacity-100'}`}
                          data-testid={`product-thumb-${idx}`}
                        >
                          <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {lightboxOpen && <ImageLightbox images={allImages} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />}
                </>
              );
            })()}
          </div>

          <div className="space-y-6">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">{product.category}</span>
              <h1 className="text-4xl font-black font-heading mt-2 mb-4" data-testid="product-detail-name">{product.name}</h1>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-5 w-5 ${i < Math.floor(avgRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                  ))}
                  <span className="ml-2 text-sm text-muted-foreground" data-testid="average-rating">({avgRating} / {reviews.length} reviews)</span>
                </div>
              </div>
              <div className="mb-4" data-testid="product-detail-price">
                <PriceDisplay price={product.price} originalPrice={product.original_price} size="lg" />
              </div>
              <div className="mb-4" data-testid="pdp-pincode-check">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Check delivery to your pincode</p>
                <PincodeCheck testIdPrefix="pdp-pincode" />
              </div>
              <p className="text-muted-foreground leading-relaxed" data-testid="product-detail-description">{product.description}</p>
              <p className="text-sm text-muted-foreground mt-2" data-testid="product-stock">Stock: {product.stock} units available</p>
              {(product.batch_number || product.expiry_date) && (
                <div
                  className="mt-4 rounded-xl border border-[#E0E0E0] bg-[#FAFAFA] px-4 py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm"
                  data-testid="product-batch-expiry-section"
                >
                  {product.batch_number && (
                    <div className="min-w-0" data-testid="product-batch-number">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Batch Number</p>
                      <p className="font-mono font-semibold text-foreground">{product.batch_number}</p>
                    </div>
                  )}
                  {product.expiry_date && (
                    <div className="min-w-0" data-testid="product-expiry-date">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Expiry Date</p>
                      <p className="font-semibold text-foreground">
                        {(() => {
                          try {
                            return new Date(product.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                          } catch { return product.expiry_date; }
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {product.variants && product.variants.length > 0 ? (
              <div data-testid="variant-multi-selector">
                <div className="flex items-baseline justify-between mb-3">
                  <Label className="text-base font-bold">Choose Variant{product.variants.length > 1 ? 's' : ''}</Label>
                  <span className="text-xs text-muted-foreground">Pick any combination</span>
                </div>
                <div className="space-y-2">
                  {product.variants.map((variant, index) => {
                    const qty = Number(variantQuantities[index] || 0);
                    const otherQty = Object.entries(variantQuantities).reduce((s, [k, q]) => s + (k === String(index) ? 0 : Number(q || 0)), 0);
                    const remaining = Math.max(0, (product.stock || 0) - otherQty);
                    const selected = qty > 0;
                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-[#E0E0E0] hover:border-primary/40'}`}
                        data-testid={`variant-row-${index}`}
                      >
                        <div className="flex-1">
                          <div className="font-bold text-sm" data-testid={`variant-label-${index}`}>
                            {variantLabel(variant)}
                          </div>
                          {variant.price !== undefined && (
                            <div className="text-xs text-muted-foreground">{'\u20B9'}{variant.price} each</div>
                          )}
                        </div>
                        <div className="inline-flex items-center rounded-full border border-[#E0E0E0] bg-white">
                          <Button
                            size="icon" variant="ghost" className="h-8 w-8 rounded-full"
                            onClick={() => setVariantQty(index, qty - 1)}
                            disabled={qty <= 0}
                            data-testid={`variant-qty-dec-${index}`}
                            aria-label="Decrease"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </Button>
                          <input
                            type="number"
                            min={0}
                            value={qty}
                            onChange={(e) => setVariantQty(index, parseInt(e.target.value || '0', 10))}
                            className="w-10 text-center text-sm bg-transparent focus:outline-none"
                            data-testid={`variant-qty-input-${index}`}
                          />
                          <Button
                            size="icon" variant="ghost" className="h-8 w-8 rounded-full"
                            onClick={() => setVariantQty(index, qty + 1)}
                            disabled={qty >= remaining}
                            data-testid={`variant-qty-inc-${index}`}
                            aria-label="Increase"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {totalSelectedQty > 0 && (
                  <div className="mt-3 text-sm text-muted-foreground" data-testid="variant-total-summary">
                    Total: <b className="text-foreground">{totalSelectedQty}</b> item{totalSelectedQty === 1 ? '' : 's'}
                    {product.stock !== undefined && product.stock > 0 && (
                      <span className="ml-2">· {product.stock - totalSelectedQty} left in stock</span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-3" data-testid="pdp-quantity-row">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Quantity</span>
                <div className="inline-flex items-center rounded-full border border-[#E0E0E0] bg-white">
                  <Button
                    size="icon" variant="ghost" className="h-9 w-9 rounded-full"
                    onClick={() => setVariantQty('-1', (variantQuantities['-1'] || 1) - 1)}
                    disabled={(variantQuantities['-1'] || 0) <= 1}
                    data-testid="pdp-qty-decrement"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <input
                    type="number"
                    min={1}
                    max={Math.max(1, product?.stock || 1)}
                    value={variantQuantities['-1'] || 1}
                    onChange={(e) => setVariantQty('-1', Math.max(1, parseInt(e.target.value || '1', 10)))}
                    className="w-12 text-center text-sm bg-transparent focus:outline-none"
                    data-testid="pdp-qty-input"
                  />
                  <Button
                    size="icon" variant="ghost" className="h-9 w-9 rounded-full"
                    onClick={() => setVariantQty('-1', (variantQuantities['-1'] || 0) + 1)}
                    disabled={product?.stock ? (variantQuantities['-1'] || 0) >= product.stock : false}
                    data-testid="pdp-qty-increment"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {product?.stock !== undefined && product.stock <= 5 && (
                  <span className="text-xs text-amber-700" data-testid="pdp-low-stock">Only {product.stock} left</span>
                )}
              </div>
            )}

            {(() => {
              const tiers = (product?.bulk_tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
              if (!tiers.length) return null;
              const qtyForTier = totalSelectedQty || 0;
              const applicable = tiers.filter((t) => qtyForTier >= t.min_qty);
              const currentPct = applicable.length ? Math.max(...applicable.map((t) => t.discount_percent)) : 0;
              const upcoming = tiers.find((t) => t.min_qty > qtyForTier);
              const lineTotal = product.price * qtyForTier;
              const savedNow = lineTotal * (currentPct / 100);
              return (
                <div className="mb-3 p-3 rounded-lg bg-primary/5 border border-primary/15 text-xs" data-testid="pdp-bulk-tier-info">
                  {currentPct > 0 ? (
                    <p className="text-primary font-semibold" data-testid="pdp-tier-active">You save ₹{savedNow.toFixed(0)} ({currentPct}% off applied)</p>
                  ) : null}
                  {upcoming ? (
                    <p className="text-muted-foreground mt-0.5" data-testid="pdp-tier-upsell">
                      Buy <strong>{upcoming.min_qty}</strong> to save{' '}
                      <strong>₹{(product.price * upcoming.min_qty * (upcoming.discount_percent / 100)).toFixed(0)}</strong>{' '}
                      ({upcoming.discount_percent}% off the whole line)
                    </p>
                  ) : currentPct > 0 ? (
                    <p className="text-muted-foreground mt-0.5">Top tier reached — maximum savings unlocked.</p>
                  ) : null}
                </div>
              );
            })()}

            <div className="flex gap-3">
              <Button size="lg" className="flex-1 rounded-full" onClick={addToCart} data-testid="add-to-cart-button">
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Cart
              </Button>
              <Button size="lg" className="flex-1 rounded-full bg-accent hover:bg-accent/90 text-white" onClick={async () => { const ok = await addToCart(); if (ok) navigate('/cart'); }} data-testid="buy-now-button">
                <Zap className="mr-2 h-5 w-5" />
                Buy Now
              </Button>
              <Button size="lg" variant="outline" className="rounded-full" onClick={addToWishlist} data-testid="add-to-wishlist-button">
                <Heart className="h-5 w-5" />
              </Button>
            </div>

            {/* Delivery Info */}
            <div className="mt-6 p-4 rounded-xl bg-muted border border-[#E0E0E0]" data-testid="delivery-info">
              <div className="flex items-center gap-2 text-sm">
                <Truck className="h-4 w-4 text-primary flex-shrink-0" />
                {product.price >= deliveryConfig.free_threshold ? (
                  <span className="text-primary font-medium">Free delivery on this order</span>
                ) : (
                  <span>Delivery: <b>{'\u20B9'}{deliveryConfig.charge}</b> | Free on orders above {'\u20B9'}{deliveryConfig.free_threshold}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-[#E0E0E0] pt-12">
          <h2 className="text-2xl font-bold font-heading mb-8" data-testid="reviews-heading">Customer Reviews</h2>

          {user && (
            <div className="mb-8 p-6 rounded-2xl bg-white border border-[#E0E0E0]" data-testid="review-form">
              <h3 className="font-bold mb-4">Write a Review</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                {[
                  { key: 'quality', label: 'Product Quality' },
                  { key: 'packaging', label: 'Packaging' },
                  { key: 'value_for_money', label: 'Value for Money' },
                  { key: 'taste_aroma', label: 'Taste & Aroma' },
                ].map(({ key, label }) => (
                  <div key={key} data-testid={`sub-rating-${key}`}>
                    <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className={`h-5 w-5 cursor-pointer transition-colors ${star <= subRatings[key] ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} onClick={() => setSubRatings({ ...subRatings, [key]: star })} data-testid={`${key}-star-${star}`} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-muted">
                <Label className="text-sm font-semibold">Overall:</Label>
                {[1, 2, 3, 4, 5].map((star) => {
                  const avg = Math.round((subRatings.quality + subRatings.packaging + subRatings.value_for_money + subRatings.taste_aroma) / 4);
                  return <Star key={star} className={`h-5 w-5 ${star <= avg ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />;
                })}
                <span className="text-sm text-muted-foreground ml-1">{((subRatings.quality + subRatings.packaging + subRatings.value_for_money + subRatings.taste_aroma) / 4).toFixed(1)}</span>
              </div>
              <Textarea placeholder="Share your experience..." value={comment} onChange={(e) => setComment(e.target.value)} className="mb-4" data-testid="review-comment-input" />
              <Button onClick={submitReview} data-testid="submit-review-button">Submit Review</Button>
            </div>
          )}

          <div className="space-y-4">
            {reviews.map((review) => (
              <div key={review.review_id} className="p-6 rounded-2xl bg-white border border-[#E0E0E0]" data-testid={`review-${review.review_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{review.user_name}</span>
                    {review.verified_purchase && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                        title="This shopper actually bought this product on Rejumist"
                        data-testid={`verified-purchase-badge-${review.review_id}`}
                      >
                        <BadgeCheck className="h-3 w-3" />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                    ))}
                  </div>
                </div>
                {(review.quality > 0 || review.packaging > 0 || review.value_for_money > 0 || review.taste_aroma > 0) && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    {[
                      { key: 'quality', label: 'Quality' },
                      { key: 'packaging', label: 'Packaging' },
                      { key: 'value_for_money', label: 'Value' },
                      { key: 'taste_aroma', label: 'Taste' },
                    ].map(({ key, label }) => review[key] > 0 ? (
                      <div key={key} className="text-center">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
                        <div className="flex justify-center gap-0.5 mt-0.5">
                          {[...Array(5)].map((_, i) => <Star key={i} className={`h-3 w-3 ${i < review[key] ? 'text-yellow-500 fill-yellow-500' : 'text-gray-200'}`} />)}
                        </div>
                      </div>
                    ) : null)}
                  </div>
                )}
                <p className="text-muted-foreground">{review.comment}</p>
                <p className="text-xs text-muted-foreground mt-2">{new Date(review.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
              </div>
            ))}
          </div>

          {reviews.length === 0 && (
            <p className="text-center text-muted-foreground py-8" data-testid="no-reviews-message">No reviews yet. Be the first to review!</p>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
